import { SparklesIcon } from '@heroicons/react/24/outline';

// Thinking steps for AI processing visualization (matches backend PROGRESS_EVENTS)
export const thinkingSteps = [
  { step: 0, icon: '🔍', text: 'Understanding your request...' },
  { step: 1, icon: '🗂️', text: 'Identifying relevant data sources...' },
  { step: 2, icon: '🛠️', text: 'Processing with AI...' },
  { step: 3, icon: '📊', text: 'Fetching and analyzing data...' },
  { step: 4, icon: '✨', text: 'Generating response...' }
];

/**
 * ThinkingChain - shows step-by-step progress during AI processing (SSE progress events).
 * @param {number} currentStep - Current step index (0-4)
 * @param {number} elapsedTime - Elapsed time in ms
 * @param {Record<number, string>} stepDetails - Optional details per step (e.g. { 2: 'Iteration 3/15...' })
 */
export default function ThinkingChain({ currentStep, elapsedTime, stepDetails = {} }) {
  return (
    <div className="flex gap-2 sm:gap-4">
      <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center flex-shrink-0">
        <SparklesIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white animate-pulse" />
      </div>
      <div className="flex-1 max-w-xl">
        <div className="bg-surface-100 dark:bg-surface-800 rounded-2xl px-4 py-3 sm:px-5 sm:py-4">
          <div className="space-y-2.5">
            {thinkingSteps.map((step, index) => {
              const isComplete = currentStep > index;
              const isCurrent = currentStep === index;
              const isPending = currentStep < index;
              const details = stepDetails[index];

              return (
                <div
                  key={index}
                  className={`transition-all duration-300 ${
                    isPending ? 'opacity-30' : 'opacity-100'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-sm ${
                      isComplete
                        ? 'bg-green-100 dark:bg-green-900/30'
                        : isCurrent
                          ? 'bg-primary-100 dark:bg-primary-900/30 animate-pulse'
                          : 'bg-surface-200 dark:bg-surface-700'
                    }`}>
                      {isComplete ? (
                        <span className="text-green-600 dark:text-green-400">✓</span>
                      ) : (
                        <span className={isCurrent ? 'animate-bounce' : ''}>{step.icon}</span>
                      )}
                    </div>

                    <span className={`text-sm flex-1 ${
                      isComplete
                        ? 'text-green-600 dark:text-green-400'
                        : isCurrent
                          ? 'text-surface-900 dark:text-white font-medium'
                          : 'text-surface-400 dark:text-surface-500'
                    }`}>
                      {step.text}
                    </span>

                    {isCurrent && (
                      <div className="w-4 h-4 border-2 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" />
                    )}
                  </div>

                  {details != null && details !== '' && (isComplete || isCurrent) && (
                    <div className={`ml-9 mt-1 text-xs italic ${
                      isCurrent
                        ? 'text-primary-600 dark:text-primary-400 font-medium'
                        : 'text-surface-500 dark:text-surface-400'
                    }`}>
                      {isCurrent && <span className="inline-block w-1.5 h-1.5 bg-primary-500 rounded-full mr-1.5 animate-pulse" />}
                      {typeof details === 'string' ? details : details?.details ?? ''}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-4 h-1.5 bg-surface-200 dark:bg-surface-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-primary-500 to-accent-500 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${Math.min((currentStep / thinkingSteps.length) * 100 + (100 / thinkingSteps.length / 2), 100)}%` }}
            />
          </div>

          <div className="mt-2 text-xs text-surface-400 dark:text-surface-500 text-right">
            {elapsedTime < 1000 ? 'Just started...' : `${(elapsedTime / 1000).toFixed(1)}s elapsed`}
          </div>
        </div>
      </div>
    </div>
  );
}
